import Cell from "./Cell.js";
import Model from "../../Model.js";

/**
 * Hold the data for a report.
 *
 * The data is stored in nested maps by row id, then column id.
 *
 * A cell is considered as empty if its data is zero.
 *
 * @param {Report} [spec.report] - The report this dataSet applies to.
 * This is mandatory if spec.cells is undefined and useless otherwise.
 * @param {{}} [spec.cells] - Object mapping row ids to "row data".
 * Row data is represented as an object mapping column ids to instances of Cell.
 */
export default class ReportDataSet extends Model {
	constructor({ report, cells }) {
		super(...arguments);

		this._cells = cells || {};

		if (!cells) {
			const rows = report.getAllRows();

			if (rows.length) {
				const leafColumns = report.getLeafColumns();

				rows.forEach((row) => {
					leafColumns.forEach((column) => {
						this._addCell(new Cell({ row, column, report }));
					});
				});
			}
		}
	}

	/**
	 * Return the cell storing data for `row` and `column`. If no the
	 * report doesn't hold `row` or `column`, return `null`.
	 */
	getCell({ column, row }) {
		const rowCells = this._cells[row.getId()];

		if (!rowCells) {
			return null;
		}

		return rowCells[column.getId()];
	}

	/**
	 * Set the data for the cell at `column` and `row`.
	 */
	setCellData({ column, row, data }) {
		this.getCell({ column, row }).setData(data);
	}

	/**
	 * Return the raw data for `column` and `row`.
	 */
	getData({ column, row }) {
		const cell = this.getCell({ column, row });
		return cell && cell.getData();
	}

	/**
	 * Return true if `row` has a data for any column of the report.
	 * Zero is considered as no data.
	 */
	hasDataForRow(row) {
		const cells = this._cellsForRow(row);
		return cells && cells.some((cell) => cell.getData());
	}

	/**
	 * Return true if `column` has data for any row of the report.
	 * Zero is considered as no data.
	 */
	hasDataForColumn(column) {
		const cells = this._cellsForColumn(column);
		return cells && cells.some((cell) => cell.getData());
	}

	getDataForRow(row) {
		const cells = this._cellsForRow(row);
		return cells && cells.map((cell) => cell.getData());
	}

	_addCell(cell) {
		let rowCells = this._cells[cell.getRowId()];
		rowCells ||= this._cells[cell.getRowId()] = {};

		rowCells[cell.getColumnId()] = cell;
	}

	_cellsForRow(row) {
		const cells = this._cells[row.getId()];
		return cells && Object.values(cells);
	}

	_cellsForColumn(column) {
		return Object.values(this._cells).map(
			(rowCells) => rowCells[column.getId()],
		);
	}

	static transformMaterializationData(data, { rows, columns }) {
		const cells = {};

		rows.forEach((row, rowIndex) => {
			cells[row.getId()] = {};
			const rowData = data.cells[rowIndex];
			if (rowData) {
				columns.forEach((column, columnIndex) => {
					const cellData = rowData[columnIndex];
					if (cellData) {
						cells[row.getId()][column.getId()] = new Cell({
							column,
							row,
							data: cellData.value,
						});
					}
				});
			}
		});

		data.cells = cells;
	}
}
